import { Link } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import type { Ticket, TicketStatus, TicketCategory } from "./types";
import { STATUS_DOT_COLORS, STATUS_TEXT_COLORS, STATUS_LABELS, CATEGORY_LABELS } from "./constants";

const SKELETON_ROWS = 5;

function StatusDot({ status }: { status: TicketStatus }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium font-mono-data"
      style={{ color: STATUS_TEXT_COLORS[status] }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ backgroundColor: STATUS_DOT_COLORS[status], boxShadow: `0 0 6px ${STATUS_DOT_COLORS[status]}` }}
      />
      {STATUS_LABELS[status]}
    </span>
  );
}

function CategoryChip({ category }: { category: TicketCategory }) {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium font-mono-data"
      style={{
        background: 'oklch(0.65 0.22 285 / 0.1)',
        color: 'oklch(0.65 0.22 285)',
        border: '1px solid oklch(0.65 0.22 285 / 0.25)',
      }}
    >
      {CATEGORY_LABELS[category]}
    </span>
  );
}

function SortIcon({ sorted }: { sorted: false | "asc" | "desc" }) {
  if (sorted === "asc") return <ChevronUp className="ml-1 h-3.5 w-3.5" />;
  if (sorted === "desc") return <ChevronDown className="ml-1 h-3.5 w-3.5" />;
  return <ChevronsUpDown className="ml-1 h-3.5 w-3.5 opacity-40" />;
}

function SkeletonTable() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-16">#</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead>From</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Assigned</TableHead>
          <TableHead>Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <TableRow key={i}>
            <TableCell><Skeleton className="h-4 w-8" /></TableCell>
            <TableCell><Skeleton className="h-4 w-48" /></TableCell>
            <TableCell><Skeleton className="h-4 w-36" /></TableCell>
            <TableCell><Skeleton className="h-5 w-16 rounded" /></TableCell>
            <TableCell><Skeleton className="h-5 w-20 rounded" /></TableCell>
            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const columnHelper = createColumnHelper<Ticket>();

const columns = [
  columnHelper.accessor("id", {
    id: "id",
    header: "#",
    enableSorting: false,
    cell: (info) => (
      <span className="text-muted-foreground font-mono-data text-xs">
        #{info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("subject", {
    id: "subject",
    header: "Subject",
    enableSorting: true,
    cell: (info) => (
      <Link
        to={`/tickets/${info.row.original.id}`}
        className="font-medium max-w-xs truncate block hover:text-primary transition-colors"
      >
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.display({
    id: "from",
    header: "From",
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-sm">{row.original.fromName}</span>
        <span className="text-xs text-muted-foreground font-mono-data">{row.original.fromEmail}</span>
      </div>
    ),
  }),
  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    enableSorting: true,
    cell: (info) => <StatusDot status={info.getValue()} />,
  }),
  columnHelper.accessor("category", {
    id: "category",
    header: "Category",
    enableSorting: true,
    cell: (info) => {
      const cat = info.getValue();
      return cat ? (
        <CategoryChip category={cat} />
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      );
    },
  }),
  columnHelper.accessor("assignedAgent", {
    id: "assignedAgent",
    header: "Assigned",
    enableSorting: true,
    cell: (info) => (
      <span className="text-sm text-muted-foreground">
        {info.getValue()?.name ?? "—"}
      </span>
    ),
  }),
  columnHelper.accessor("createdAt", {
    id: "createdAt",
    header: "Created",
    enableSorting: true,
    cell: (info) => (
      <span className="text-xs text-muted-foreground whitespace-nowrap font-mono-data">
        {new Date(info.getValue()).toLocaleDateString()}
      </span>
    ),
  }),
];

export function TicketsTable({
  tickets,
  isLoading,
  sorting,
  onSortingChange,
}: {
  tickets: Ticket[];
  isLoading: boolean;
  sorting: SortingState;
  onSortingChange: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
}) {
  const table = useReactTable({
    data: tickets,
    columns,
    state: { sorting },
    onSortingChange,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <SkeletonTable />;

  if (tickets.length === 0) {
    return <p className="text-sm text-muted-foreground">No tickets found.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    className={canSort ? "cursor-pointer select-none" : undefined}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  >
                    <span className="inline-flex items-center">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && <SortIcon sorted={sorted} />}
                    </span>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
